import Button from '@/components/Button';
import Footer from '@/components/Footer';
import Navbar from '@/components/Navbar';
import { animationConfig, getPageAnimation } from '@/config/animations';
import { motion } from 'framer-motion';
import { Layout, Palette, Sparkles, Wand2, Zap } from 'lucide-react';
import { useState } from 'react';

const ProductsPage = () => {
  const AnimationWrapper = animationConfig.enablePageTransitions ? motion.div : 'div';
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const apiUrl = import.meta.env.VITE_API_URL;

  const handleWaitlistSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccess(false);

    try {
      const response = await fetch(`${apiUrl}api/waitlist/join`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess(true);
        setEmail('');
        setTimeout(() => setSuccess(false), 5000);
      } else {
        setError(data.message || 'Failed to join waitlist. Please try again.');
      }
    } catch (err) {
      setError('Failed to join waitlist. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const features = [
    {
      icon: Palette,
      title: 'AI-Powered Image Generation',
      description: 'Create stunning visuals with state-of-the-art AI models while maintaining your creative vision and style.',
    },
    {
      icon: Wand2,
      title: 'Smart Editing Tools',
      description: 'Enhance and refine your creations with intelligent editing capabilities that understand context.',
    },
    {
      icon: Layout,
      title: 'Creative Workflows',
      description: 'Streamlined workflows designed for creators, from concept to final output.',
    },
    {
      icon: Sparkles,
      title: 'Style Consistency',
      description: 'Maintain consistent visual styles across all your projects with AI assistance.',
    },
  ];

  const useCases = [
    {
      title: 'Digital Artists',
      description: 'Explore new creative possibilities and speed up your ideation process.',
    },
    {
      title: 'Content Creators',
      description: 'Generate compelling visual content for social media and marketing.',
    },
    {
      title: 'Designers',
      description: 'Rapidly prototype concepts and iterate on design ideas.',
    },
    {
      title: 'Marketing Teams',
      description: 'Create on-brand visual assets quickly and consistently.',
    },
  ];

  return (
    <AnimationWrapper
      {...getPageAnimation()}
      className="min-h-screen bg-gradient-to-b from-dark-950 via-dark-900 to-dark-950"
    >
      <Navbar />

      <section className="pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <div className="inline-flex p-3 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 mb-6">
              <Palette className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              <span className="text-gradient">VisualForge</span>
            </h1>
            <p className="text-2xl text-primary-400 mb-4">
              AI-Powered Creative Platform
            </p>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              We're building an AI platform designed specifically for creators who want to leverage
              AI without losing their artistic voice. Currently in active development.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="glass-effect rounded-3xl p-12 mb-16"
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl font-bold text-white mb-6">
                  What We're Building
                </h2>
                <p className="text-gray-300 mb-4">
                  VisualForge is an AI-powered creative platform that helps artists, designers, and
                  content creators amplify their creativity with cutting-edge AI tools.
                </p>
                <p className="text-gray-300 mb-6">
                  Unlike generic AI tools, VisualForge is designed with creators in mind — giving you
                  control, maintaining your style, and enhancing your workflow without replacing your
                  creative vision.
                </p>
                <div className="glass-effect p-6 rounded-xl">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-300 font-medium">Development Progress</span>
                    <span className="text-primary-400">45% Complete</span>
                  </div>
                  <div className="w-full bg-dark-800 rounded-full h-3">
                    <div className="bg-gradient-to-r from-primary-500 to-primary-600 h-3 rounded-full" style={{ width: '45%' }}></div>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {features.map((feature, idx) => (
                  <div key={idx} className="glass-effect p-6 rounded-xl">
                    <feature.icon className="w-8 h-8 text-primary-400 mb-3" />
                    <h3 className="text-white font-semibold mb-2">{feature.title}</h3>
                    <p className="text-gray-400 text-sm">{feature.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <h2 className="text-3xl font-bold text-white text-center mb-12">
              Perfect For
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {useCases.map((useCase, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="glass-effect p-6 rounded-xl text-center"
                >
                  <Zap className="w-10 h-10 text-primary-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">
                    {useCase.title}
                  </h3>
                  <p className="text-gray-400 text-sm">
                    {useCase.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center glass-effect rounded-3xl p-12"
          >
            <Sparkles className="w-12 h-12 text-primary-400 mx-auto mb-6" />
            <h2 className="text-3xl font-bold text-white mb-4">
              Join the Waitlist
            </h2>
            <p className="text-gray-400 mb-8 max-w-2xl mx-auto">
              Be among the first to access VisualForge when we launch. Get early access,
              exclusive updates, and special launch pricing.
            </p>
            <form onSubmit={handleWaitlistSubmit} className="max-w-md mx-auto">
              <div className="flex gap-4">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  disabled={submitting}
                  className="flex-1 px-6 py-4 rounded-lg bg-dark-800 border border-dark-700 text-white focus:border-primary-500 focus:outline-none transition-colors disabled:opacity-50"
                />
                <Button type="submit" size="lg" disabled={submitting}>
                  {submitting ? 'Joining...' : success ? 'Joined!' : 'Join Waitlist'}
                </Button>
              </div>
              {error && (
                <div className="mt-3 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
                  {error}
                </div>
              )}
              {success && (
                <div className="mt-3 p-3 bg-green-500/10 border border-green-500/20 rounded-lg text-green-400 text-sm">
                  Welcome to the waitlist! Check your email for confirmation.
                </div>
              )}
            </form>
          </motion.div>
        </div>
      </section>

      <Footer />
    </AnimationWrapper>
  );
};

export default ProductsPage;